import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { buildPrintPreviewHtml, sanitizePdfFileNamePart } from '@/utils/printPreview'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: form_frmCtrlSalesDispatchPrint.btnPrintDispatch_Click
// (`DoCmd.OpenReport "rpCtrlSalesDispatch"` hosted in frmCtrlSalesDispatchPrint).
// Presentational dialog: parent fetches the HTML (BE render_ctrl_sales_dispatch_html)
// and passes it via props. Mirrors DeliveryPrintDialog — iframe srcDoc preview +
// Print → printAndUploadReport (PDF + archive + browser print dialog).

interface DispatchPrintDialogProps {
  open: boolean
  /** Dialog heading (e.g. `Cover Letter (4763TNC)`). */
  title: string
  /** DO no + sale_contract của row — đặt tên file "<do no>_<contract no>_CoverLetter.pdf". */
  doNo?: string
  saleContract?: string
  /** Fully-built preview HTML (PRINT_PREVIEW_CSS injected here). */
  html: string
  /** Show centered spinner while parent is fetching the HTML. */
  loading: boolean
  canEdit?: boolean
  onClose: () => void
}

export default function DispatchPrintDialog({
  open,
  title,
  doNo = '',
  saleContract = '',
  html,
  loading,
  canEdit = true,
  onClose,
}: DispatchPrintDialogProps) {
  const [toastMessage, setToastMessage] = useState('')
  const iframeTitle = 'Dispatch Cover Letter Print Preview'
  // Cover Letter = report có bảng Docs co giãn → BE trả nhiều tờ `.dispatch-page`
  // (bảng nhiều dòng tự break trang). `pageSelector` cho mỗi .dispatch-page thành 1
  // tờ A4 riêng trên nền xám (preview tách trang = PDF).
  const previewHtml = html
    ? buildPrintPreviewHtml(html, {
        styleId: 'dispatch-print-preview-overrides',
        pageSelector: '.dispatch-page',
        pagePadding: '9mm 6mm',
        extraCss: '.dispatch-page + .dispatch-page { break-before: page; page-break-before: always; }',
      })
    : ''

  return (
    <>
      {open && loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog
        open={open && Boolean(previewHtml)}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={title}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            {title}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} size="small">
              Close
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  // "Cover Letter (5784TNC)" → "5784TNC_CoverLetter.pdf" (id trong ngoặc đưa lên đầu).
                  // Tên file = "<contract no>_<do no>_CoverLetter.pdf" (fallback: id trong ngoặc title).
                  const fallbackId = title.replace(/^.*\(([^)]+)\).*$/, '$1')
                  const idPart = [saleContract.trim(), doNo.trim()].filter(Boolean).join('_') || fallbackId
                  const printTitle = `${sanitizePdfFileNamePart(idPart || 'dispatch')}_CoverLetter.pdf`
                  void printAndUploadReport({
                    iframeTitle,
                    reportName: 'rpCtrlSalesDispatch',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
